// --------------------- Import Globais ---------------------
import { useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
// --------------------- Import Antd-Design ---------------------

import {
  Col,
  Flex,
  Row,
  MenuProps,
  Skeleton,
  Table,
  Typography,
  Button,
} from "antd";

// --------------------- Import Material Icon ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";

// --------------------- Import Styles ---------------------
import { Container } from "./styles";

// --------------------- Import Hooks ---------------------
import { useError } from "./hooks/useError";
import { useSuccess } from "./hooks/useSuccess";
import { useAssinaturas } from "./hooks/useAssinaturas";

import { usePermissao } from "./hooks/usePermissao";
import { reenviarWhatsappAssinaturaService } from "./services/reenviarWhatsappAssinaturaService";

// --------------------- Import Constants ---------------------
//import { items } from "./constants/itemsActionColumns";

// --------------------- Import Components ---------------------
import { Columns } from "./components/Columns";
import ModalAtivarAssinatura from "./components/ModalAtivarAssinatura";
import ModalDesativarAssinatura from "./components/ModalDesativarAssinatura";
import ModalAdicionar from "./components/ModalAdicionar";
import ModalAndamentosAssinatura from "./components/ModalAndamentosAssinatura";
import ModalVincularCotaAssinatura from "./components/ModalVincularCotaAssinatura";
import ModalCancelarAssinatura from "./components/ModalCancelarAssinatura";
// import ModalHospedes from "./components/ModalHospedes";

// ---------------------  ---------------------

export default function ViewGerenciarAssinatura() {
  // Traz o estilo Title da biblioteca
  const { Title } = Typography;

  // Exibe a Mensagem de  Erro
  const { showError, contextHolder: errorContextHolder } = useError();

  // Exibe a Mensagem de sucesso
  const { showSuccess, contextHolder: successContextHolder } = useSuccess();

  // Realiza a busca das assinaturas ativas
  const {
    assinaturas,
    isLoading: isReservasLoading,
    fetchAssinaturas,
    upsertAssinatura,
  } = useAssinaturas();

  // Verificacao de permissao do usuario, NDA = 0 - SEM PERMISSAO, NDA = 1 - COM PERMISSAO
  // Definicao do Loading caso seja permitido
  const { isLoading: isTokenLoading, isValid } = usePermissao();

  // Controle de Paginacao
  const currentPage = useRef(1);

  // Armazena o valor da assinatura selecionada
  const [assinaturaId, setAssinaturaId] = useState<number | null>(null);

  // Armazena o valor do status selecionado
  const [status, setStatus] = useState<string | null>(null);

  // Armazena o valor da cotaUsuario selecionada
  const [cotaUsuarioId, setCotaUsuarioId] = useState<number | null>(null);

  // Armazena o valor da pessoa vinculada a assinatura
  const [pessoaId, setPessoaId] = useState<number | null>(null);

  const [isReenviandoMensagem, setIsReenviandoMensagem] = useState(false);

  // ------------------- MODAL -----------------
  // State de abertura e fechamento do modal ativar assinatura
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Funcao para abrir o modal ativar assinatura
  const showModal = () => {
    setIsModalOpen(true);
  };

  // Funcao para fechar o modal ativar assinatura
  const handleFechar = () => {
    setIsModalOpen(false);
  };

  // State de abertura e fechamento do modal excluir assinatura
  const [isModalDesativarOpen, setIsModalDesativarOpen] = useState(false);

  // Funcao para abrir o modal excluir assinatura
  const showModalDesativar = () => {
    setIsModalDesativarOpen(true);
  };

  // Funcao para fechar o modal excluir assinatura
  const handleFecharDesativar = () => {
    setIsModalDesativarOpen(false);
  };

  const [isModalAndamentosOpen, setIsModalAndamentosOpen] = useState(false);

  const handleAbrirModalAndamentos = () => {
    setIsModalAndamentosOpen(true);
  };

  const handleFecharModalAndamentos = () => {
    setIsModalAndamentosOpen(false);
  };

  const [isModalVincularCotaOpen, setIsModalVincularCotaOpen] =
    useState(false);

  const handleAbrirModalVincularCota = () => {
    setIsModalVincularCotaOpen(true);
  };

  const handleFecharModalVincularCota = () => {
    setIsModalVincularCotaOpen(false);
  };

  const [isModalCancelarOpen, setIsModalCancelarOpen] = useState(false);

  const handleAbrirModalCancelar = () => {
    setIsModalCancelarOpen(true);
  };

  const handleFecharModalCancelar = () => {
    setIsModalCancelarOpen(false);
  };

  // ------------------------------  ---------------------------

  const handleReenviarMensagem = async () => {
    if (!assinaturaId) {
      showError("Nao foi possivel identificar a assinatura selecionada.");
      return;
    }

    setIsReenviandoMensagem(true);

    try {
      const response = await reenviarWhatsappAssinaturaService(assinaturaId);

      if (response.whatsapp?.enviado) {
        showSuccess("Mensagem reenviada com sucesso!");
      } else if (response.whatsapp?.tentado) {
        showError("A cobranca foi localizada, mas o WhatsApp nao foi enviado.");
      } else {
        showError("Nao foi possivel reenviar a mensagem.");
      }

      await fetchAssinaturas(false);
    } catch (error) {
      const backendMessage = (error as any)?.response?.data?.message;

      if (Array.isArray(backendMessage) && backendMessage.length > 0) {
        showError(backendMessage[0]);
      } else if (typeof backendMessage === "string") {
        showError(backendMessage);
      } else if (error instanceof Error && error.message) {
        showError(error.message);
      } else {
        showError("Nao foi possivel reenviar a mensagem.");
      }
    } finally {
      setIsReenviandoMensagem(false);
    }
  };

  // OnClick das opcoes e funcoes que serao realizadas do item Acoes
  const onClick: MenuProps["onClick"] = async ({ key }) => {
    switch (key) {
      // Verifica se o status ja esta ativo, se estiver emite um alerta indicando que a assinatura ja esta ativa, se nao abre o modal para ativacao
      case "1":
        if (status === "ATIVA") {
          showError("Essa assinatura ja esta ativa!");
        } else {
          showModal();
        }

        break;
      // Abre o modal para a exclusao da assinatura
      case "2":
        if (status === "PENDENTE") {
          showModalDesativar();
        } else {
          showError(
            "Nao e possivel excluir essa assinatura! Esta assinatura esta ativa"
          );
        }

        break;
      case "3":
        if (status !== "PENDENTE") {
          showError(
            "So e possivel reenviar a mensagem para assinaturas pendentes."
          );
        } else {
          await handleReenviarMensagem();
        }

        break;
      case "4":
        if (!assinaturaId) {
          showError("Nao foi possivel identificar a assinatura selecionada.");
        } else {
          handleAbrirModalAndamentos();
        }

        break;
      case "5":
        if (!assinaturaId) {
          showError("Nao foi possivel identificar a assinatura selecionada.");
        } else if (!pessoaId) {
          showError("Nao foi possivel identificar a pessoa da assinatura.");
        } else {
          handleAbrirModalVincularCota();
        }

        break;
      case "6":
        if (!assinaturaId) {
          showError("Nao foi possivel identificar a assinatura selecionada.");
        } else if (status !== "ATIVA") {
          showError("So e possivel cancelar assinaturas ativas.");
        } else {
          handleAbrirModalCancelar();
        }

        break;
    }
  };

  // Traz as informacoes OnClick, status e assinaturaId ao clicar na linha especifica
  const columns = Columns(
    onClick,
    setStatus,
    setCotaUsuarioId,
    setAssinaturaId,
    setPessoaId
  );

  const isMobile = useMediaQuery("(max-width:600px)");

  const [openModalAdicionar, setOpenModalAdicionar] = useState(false);

  const handleAbrirModalAdicionar = () => {
    setOpenModalAdicionar(true);
  };

  const handleFecharModalAdicionar = () => {
    setOpenModalAdicionar(false);
  };

  // ---------------------  ---------------------
  return (
    <>
      {isTokenLoading || !isValid || isReservasLoading ? (
        <Skeleton active />
      ) : (
        <Container>
          <>{errorContextHolder}</>
          <>{successContextHolder}</>

          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a href={`/inicio`}>
                  <ArrowBackIcon style={{ color: "#1976d2" }} />
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />

                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Gerenciar Assinatura
                </Title>
              </Flex>
            </Col>
          </Row>
          <Row>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                height: "auto",
                width: "90%",
                gap: "20px",
              }}
            >
              <div style={{ display: "flex", gap: "20px" }}>
                <Button
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "auto",
                  }}
                  onClick={handleAbrirModalAdicionar}
                  type="primary"
                >
                  + Assinatura
                </Button>

                {/* {semFiltros ? (
                              <Button onClick={handleFilter} type="primary" disabled>
                                <FilterAltIcon />
                              </Button>
                            ) : (
                              <Button onClick={handleFilter} type="primary">
                                <FilterAltOffIcon />
                              </Button>
                            )} */}
              </div>
            </div>
            <Col span={isMobile ? 5 : 1}></Col>
            <Col
              span={22}
              style={{
                marginTop: isMobile ? 15 : 20,
                marginBottom: isMobile ? 30 : 50,
              }}
            >
              <Flex
                style={{
                  width: isMobile ? "auto" : "100%",
                  overflow: "auto",
                  marginLeft: 30,
                }}
              >
                <Table
                  columns={columns}
                  dataSource={assinaturas}
                  loading={isReenviandoMensagem}
                  bordered
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: isMobile ? "auto" : "100%",
                    maxWidth: isMobile ? "none" : "100%",
                  }}
                  pagination={{ defaultCurrent: currentPage.current }}
                />
              </Flex>
            </Col>
            <Col span={1}></Col>
          </Row>
          <ModalAtivarAssinatura
            isModalOpen={isModalOpen}
            handleFechar={handleFechar}
            assinaturaId={assinaturaId}
            cotaUsuarioId={cotaUsuarioId}
          />
          <ModalDesativarAssinatura
            isModalOpen={isModalDesativarOpen}
            handleFechar={handleFecharDesativar}
            assinaturaId={assinaturaId}
            cotaUsuarioId={cotaUsuarioId}
          />
          <ModalAdicionar
            isModalOpen={openModalAdicionar}
            handleFechar={handleFecharModalAdicionar}
            fetch={fetchAssinaturas}
            onAssinaturaCriada={upsertAssinatura}
          />
          <ModalAndamentosAssinatura
            isModalOpen={isModalAndamentosOpen}
            handleFechar={handleFecharModalAndamentos}
            assinaturaId={assinaturaId}
          />
          <ModalVincularCotaAssinatura
            isModalOpen={isModalVincularCotaOpen}
            handleFechar={handleFecharModalVincularCota}
            assinaturaId={assinaturaId}
            assinaturaStatus={status}
            pessoaId={pessoaId}
            cotaUsuarioIdAtual={cotaUsuarioId}
            fetchAssinaturas={fetchAssinaturas}
          />
          <ModalCancelarAssinatura
            isModalOpen={isModalCancelarOpen}
            handleFechar={handleFecharModalCancelar}
            assinaturaId={assinaturaId}
            onCancelamentoConcluido={() => fetchAssinaturas(false)}
            showSuccess={showSuccess}
            showError={showError}
          />
        </Container>
      )}
    </>
  );
}
